import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { InfoPageLayout } from "@/components/pages/InfoPageLayout";
import { Mail, Bug, Sparkles, Handshake, BookOpen, Clock } from "lucide-react";
import { ContactForm } from "@/components/contact/ContactForm";
import {
  webPageSchema, breadcrumbSchema, organizationSchema, ldScripts,
} from "@/lib/seo/schema";

const TITLE = "Contact - Indonesian Calendar";
const DESC = "Contact the Indonesian Calendar team for questions, suggestions, holiday data corrections, or partnerships.";

export const Route = createFileRoute("/en/contact")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Contact Indonesian Calendar" },
      { property: "og:description", content: "Reach out for questions, feedback, or corrections." },
    ],
    links: [{ rel: "canonical", href: "https://hari.co.id/en/contact/" }],
    scripts: ldScripts([
      webPageSchema({ url: "/en/contact", name: TITLE, description: DESC, type: "ContactPage", locale: "en" }),
      breadcrumbSchema([
        { name: "Home", url: "/en" },
        { name: "Contact", url: "/en/contact" },
      ]),
      organizationSchema("en"),
    ]),
  }),
  component: ContactPage,
});

const TOPICS = [
  { icon: BookOpen, label: "Holiday data corrections" },
  { icon: Sparkles, label: "Feature requests" },
  { icon: Bug, label: "Bug reports" },
  { icon: Handshake, label: "Partnership / ads" },
];

function ContactPage() {
  return (
    <SiteShell locale="en">
      <InfoPageLayout
        variant="contact"
        title="Contact"
        subtitle="Suggestions, corrections, or partnerships. We'd love to hear from you."
        badge="@"
        locale="en"
        breadcrumbs={[{ label: "Home", href: "/en" }, { label: "Contact" }]}
        aside={
          <div className="rounded-xl border border-border bg-card p-4 text-sm">
            <h3 className="mb-2 font-semibold">More</h3>
            <ul className="space-y-1.5 text-muted-foreground">
              <li><a className="hover:text-primary" href="/en/about">About</a></li>
              <li><a className="hover:text-primary" href="/en/privacy-policy">Privacy Policy</a></li>
              <li><a className="hover:text-primary" href="/en/terms">Terms &amp; Conditions</a></li>
            </ul>
          </div>
        }
      >
        <p>
          Thank you for using <strong>Indonesian Calendar</strong>. If you spot
          a data error, want to suggest a feature, or propose a partnership,
          please reach out via the channels below.
        </p>

        <div className="not-prose mt-6 grid gap-3 sm:grid-cols-2">
          <a
            href="mailto:support@hari.co.id"
            className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Mail className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-xs uppercase tracking-wide text-muted-foreground">Email</span>
              <span className="block font-semibold">support@hari.co.id</span>
            </span>
          </a>
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Clock className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-xs uppercase tracking-wide text-muted-foreground">Response time</span>
              <span className="block font-semibold">2–5 business days</span>
            </span>
          </div>
        </div>

        <h2 id="topics">Topics we welcome</h2>
        <div className="not-prose mt-3 flex flex-wrap gap-2">
          {TOPICS.map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-sm">
              <Icon className="h-3.5 w-3.5 text-primary" /> {label}
            </span>
          ))}
        </div>

        <h2 id="form">Send a message</h2>
        <p>
          Fill in the form below. Your message will be delivered straight to
          the hari.co.id support team via your email app.
        </p>
        <ContactForm locale="en" />
      </InfoPageLayout>
    </SiteShell>
  );
}